import { faExternalLinkSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { Anchor } from "~/components/Anchor";
import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { AssetColumns } from "~/modules/assets/types";
import { parseCIK } from "~/utils/parseCIK";
import { AssetTableRowData } from "../types";
import { TooltipPassthru } from "../TooltipPassthru";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";

// // // //

function TreasuryCustodianWithLeiOrCik(props: {
    item: AssetTableRowData;
    justify: "left" | "right";
}) {
    const { item, justify = "left" } = props;
    if (item.isAsset) {
        const custodian = item.traditional_custodian;
        if (!custodian) return <Emdash />;

        // Get LEI + CIK values
        const lei = custodian.lei;
        const cik = parseCIK(custodian.cik);

        // Just display name if there are no LEI or CIK values
        if (!lei && !cik) {
            return <>{custodian.legal_name || custodian.name}</>;
        }

        // Display CIK value w/ link
        if (custodian.cik) {
            return (
                <div
                    className={cn("flex flex-col justify-center w-full", {
                        "items-start": justify === "left",
                        "items-end": justify === "right",
                    })}
                >
                    <div className="dg-overflow">
                        {custodian.legal_name || custodian.name}
                    </div>

                    <TooltipPassthru tooltipContent="Click to view on SEC Edgar Search">
                        <Anchor
                            href={`https://www.sec.gov/edgar/search/#/entityName=${cik}`}
                            analyticsLabel="Asset Table - SEC Edgar CIK Lookup"
                            variant="default"
                            className="font-mono text-xs flex items-center gap-1"
                            openInNewTab
                        >
                            {cik}
                            <FontAwesomeIcon
                                icon={faExternalLinkSquare}
                                className="h-3"
                            />
                        </Anchor>
                    </TooltipPassthru>
                </div>
            );
        }

        // Display LEI value w/ link
        return (
            <div className="flex items-start flex-col justify-center w-full">
                <div className="dg-overflow">
                    {custodian.legal_name || custodian.name}
                </div>

                <TooltipPassthru tooltipContent="Click to view on LEI-Lookup">
                    <Anchor
                        href={`https://www.lei-lookup.com/record/${lei}`}
                        analyticsLabel="Asset Table - LEI Lookup Link"
                        variant="default"
                        className="font-mono text-xs flex items-center gap-1"
                        openInNewTab
                    >
                        {lei}
                        <FontAwesomeIcon
                            icon={faExternalLinkSquare}
                            className="h-3"
                        />
                    </Anchor>
                </TooltipPassthru>
            </div>
        );
    }

    return <Emdash />;
}

export const TRADITIONAL_CUSTODIAN_WITH_LEI_OR_CIK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "custodian_legal_identifier",
    label: (
        <>
            <p>Custodian</p>
            <p className="text-xs">Legal Identifier</p>
        </>
    ),
    tooltip: CatalogReferenceDataSlugs.traditional_custodian,
    type: ColumnTypes.string,
    sortsOn: "traditional_custodian" as AssetColumns,
    width: 275,
    loginRequired: true,
    render: (item) => {
        return <TreasuryCustodianWithLeiOrCik item={item} justify="left" />;
    },
};

export const TRADITIONAL_CUSTODIAN_WITH_LEI_OR_CIK_INLINE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    ...TRADITIONAL_CUSTODIAN_WITH_LEI_OR_CIK,
    render: (item) => {
        return <TreasuryCustodianWithLeiOrCik item={item} justify="right" />;
    },
};

// // // //

export const TRADITIONAL_CUSTODIAN_LEGAL_STRUCTURE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "custodian_legal_structure",
    label: "Custodian Legal Structure",
    type: ColumnTypes.string,
    width: 450,
    cellValue: (item) => {
        if (item.isAsset) {
            const custodian = item.traditional_custodian;
            if (!custodian) return null;

            return custodian.legal_structure_type || null;
        }

        return null;
    },
};

export const TRADITIONAL_CUSTODIAN_LEGAL_COUNTRY: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "custodian_legal_structure_country",
    label: "Custodian Legal Structure Country",
    type: ColumnTypes.string,
    width: 450,
    cellValue: (item) => {
        if (item.isAsset) {
            const custodian = item.traditional_custodian;
            if (!custodian) return null;
            if (!custodian.legal_structure_country) return null;

            return custodian.legal_structure_country;
        }

        return null;
    },
};
